import styled from 'styled-components';

import SectionHeader from '../ui/SectionHeader';
import Reveal from '../ui/Reveal';
import { translations } from '../../data/i18n';
import { siteConfig } from '../../data/site';
import { buildSimpleWhatsAppUrl } from '../../utils/whatsapp';

const Section = styled.section`
  padding: 98px 28px;
  background:
    radial-gradient(circle at top left, rgba(221,161,47,0.12), transparent 34%),
    var(--cream-soft);
`;

const Inner = styled.div`
  max-width: var(--container);
  margin: 0 auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 28px;
  align-items: stretch;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  background: var(--cream-card);
  border-radius: 26px;
  padding: clamp(28px, 4vw, 42px);
  box-shadow: var(--shadow-soft);
  border: 1px solid rgba(221,161,47,0.12);

  h3 {
    margin: 0 0 18px;
    color: var(--black);
    font-size: 1.45rem;
    text-transform: uppercase;
  }

  p {
    color: var(--muted);
    line-height: 1.75;
  }
`;

const ContactList = styled.div`
  display: grid;
  gap: 14px;
  margin-top: 24px;
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 14px;

  padding: 15px 16px;
  border-radius: 16px;

  background: rgba(221,161,47,0.08);
  border: 1px solid rgba(221,161,47,0.14);

  color: var(--text);
  font-weight: 850;
  transition: transform 0.2s ease, background 0.2s ease;

  span {
    width: 34px;
    height: 34px;
    display: grid;
    place-items: center;
    border-radius: 999px;
    background: rgba(221,161,47,0.18);
  }

  &:hover {
    transform: translateY(-2px);
    background: rgba(221,161,47,0.13);
  }
`;

const Hours = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 18px;
`;

const HourRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(0,0,0,0.08);

  strong {
    color: var(--black);
  }

  span {
    color: var(--muted);
    text-align: right;
  }
`;

const AddressBox = styled.div`
  margin-top: 24px;
  padding: 20px;
  border-radius: 18px;
  background:
    linear-gradient(135deg, rgba(255,255,255,0.9), rgba(255,248,236,0.86));
  border: 1px solid rgba(221,161,47,0.14);

  strong {
    display: block;
    color: var(--black);
    margin-bottom: 8px;
  }

  p {
    margin: 0;
  }
`;

function ContactSection({ language }) {
  const t = translations[language].contact;
  const isEnglish = language === 'en';

  const whatsappText = isEnglish
    ? 'Hello Hakuna Matata, I would like to ask about an appointment.'
    : 'Hallo Hakuna Matata, ich möchte gerne wegen eines Termins anfragen.';

  const whatsappUrl = buildSimpleWhatsAppUrl({
    whatsappNumber: siteConfig.whatsappNumber,
    message: whatsappText,
  });

  return (
    <Section id="contact">
      <Inner>
        <Reveal>
          <SectionHeader eyebrow={t.subtitle} title={t.title} />
        </Reveal>

        <Grid>
          <Reveal direction="right">
            <Card>
              <h3>{t.aboutTitle}</h3>
              <p>{t.about}</p>

              <AddressBox>
                <strong>{siteConfig.address.name}</strong>
                <p>
                  {siteConfig.address.street}<br />
                  {siteConfig.address.zip} {siteConfig.address.city}<br />
                  {siteConfig.address.country}
                </p>
              </AddressBox>

              <ContactList>
                <ContactItem href={whatsappUrl} target="_blank" rel="noreferrer">
                  <span>💬</span>
                  {t.whatsapp}
                </ContactItem>

                <ContactItem href={`tel:${siteConfig.phoneHref}`}>
                  <span>📞</span>
                  {siteConfig.phoneDisplay}
                </ContactItem>

                <ContactItem href={`mailto:${siteConfig.email}`}>
                  <span>✉️</span>
                  {siteConfig.email}
                </ContactItem>

                <ContactItem href={siteConfig.address.mapsUrl} target="_blank" rel="noreferrer">
                  <span>📍</span>
                  {isEnglish ? 'Plan route' : 'Route planen'}
                </ContactItem>
              </ContactList>
            </Card>
          </Reveal>

          <Reveal direction="left" delay={120}>
            <Card>
              <h3>{t.hoursTitle}</h3>

              <Hours>
                <HourRow>
                  <strong>{isEnglish ? 'Monday' : 'Montag'}</strong>
                  <span>{isEnglish ? 'Closed' : 'Geschlossen'}</span>
                </HourRow>

                <HourRow>
                  <strong>{isEnglish ? 'Tuesday' : 'Dienstag'}</strong>
                  <span>10:00 – 18:00</span>
                </HourRow>

                <HourRow>
                  <strong>{isEnglish ? 'Wednesday' : 'Mittwoch'}</strong>
                  <span>10:00 – 18:00</span>
                </HourRow>

                <HourRow>
                  <strong>{isEnglish ? 'Thursday' : 'Donnerstag'}</strong>
                  <span>10:00 – 19:00</span>
                </HourRow>

                <HourRow>
                  <strong>{isEnglish ? 'Friday' : 'Freitag'}</strong>
                  <span>10:00 – 19:00</span>
                </HourRow>

                <HourRow>
                  <strong>{isEnglish ? 'Saturday' : 'Samstag'}</strong>
                  <span>10:00 – 16:00</span>
                </HourRow>

                <HourRow>
                  <strong>{isEnglish ? 'Sunday' : 'Sonntag'}</strong>
                  <span>{isEnglish ? 'Closed' : 'Geschlossen'}</span>
                </HourRow>
              </Hours>

              <p>
                {isEnglish
                  ? 'Appointments are currently requested via WhatsApp and confirmed personally.'
                  : 'Termine werden aktuell per WhatsApp angefragt und persönlich bestätigt.'}
              </p>
            </Card>
          </Reveal>
        </Grid>
      </Inner>
    </Section>
  );
}

export default ContactSection;